/* eslint-disable react-hooks/exhaustive-deps */
import {
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputProps,
  NumberInputStepper,
  Select,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spinner,
  Switch,
  Text,
} from "@chakra-ui/react";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";
import { AvailableLanguages } from "../language/i18n";
import {
  angleMaxValueSelector,
  angleMinValueSelector,
  angleStepSelector,
  flowRateBoundariesSelector,
  flowRateStepSelector,
  groundObstaclesState,
  maxNumberOfGroundObstaclesSelecter,
  maxNumberOfRoofObstaclesSelecter,
  monitorHeightMaxValueSelector,
  monitorHeightMinValueSelector,
  monitorHeightStepSelector,
  pressureMaxValueSelector,
  pressureMinValueSelector,
  pressureStepSelector,
  roofObstaclesState,
  selectableMonitorConfigurationsSelector,
  selectableNozzleConfigurationSelector,
  selectedAngleState,
  selectedFlowRateState,
  selectedMediumState,
  selectedMonitorHeightState,
  selectedMonitorIdState,
  selectedMonitorImageSelector,
  selectedMonitorNozzleImageSelector,
  selectedMonitorSelector,
  selectedNozzleIdState,
  selectedNozzleSelector,
  selectedPressureState,
  unitsState,
} from "../state/state";
import { ObstacleInput, Units, UnitTypes } from "../types/sharedTypes";
import { AddButton } from "./addButton";
import AngleGui from "./angleGui";
import { GrayDotBox } from "./grayDotBox";
import { ObstacleFormInput } from "./obstacleFormInput";
import { RemoveButton } from "./removeButton";
import { unitFormatter } from "../utils/units";

const Settings: FunctionComponent = () => {
  const unit = useRecoilValue(unitsState);

  const maxNumberOfRoofObstacles = useRecoilValue(
    maxNumberOfRoofObstaclesSelecter
  );

  const maxNumberOfGroundObstacles = useRecoilValue(
    maxNumberOfGroundObstaclesSelecter
  );

  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language as AvailableLanguages;

  /**
   * Monitors
   */
  const selectableMonitors = useRecoilValue(
    selectableMonitorConfigurationsSelector
  );
  const [selectedMonitorId, setSelectedMonitorId] = useRecoilState(
    selectedMonitorIdState
  );
  const selectedMonitor = useRecoilValue(selectedMonitorSelector);
  const selectedMonitorImage = useRecoilValue(selectedMonitorImageSelector);

  /**
   * Nozzles
   */
  const selectableNozzles = useRecoilValue(
    selectableNozzleConfigurationSelector
  );
  const selectedNozzle = useRecoilValue(selectedNozzleSelector);
  const [selectedNozzleId, setSelectedNozzleId] = useRecoilState(
    selectedNozzleIdState
  );
  const selectedMonitorNozzleImage = useRecoilValue(
    selectedMonitorNozzleImageSelector
  );

  /**
   * Flow Rate
   */
  const flowRateStep = useRecoilValue(flowRateStepSelector);
  const flowRateBoundaries = useRecoilValue(flowRateBoundariesSelector);
  const flowRateMinValue = flowRateBoundaries?.flowRateMinimum ?? 0;
  const flowRateMaxValue = flowRateBoundaries?.flowRateMaximum ?? 0;

  const [selectedFlowRate, setSelectedFlowRate] = useRecoilState(
    selectedFlowRateState
  );

  /**
   * Pressure
   */
  const pressureMinValue = useRecoilValue(pressureMinValueSelector);
  const pressureMaxValue = useRecoilValue(pressureMaxValueSelector);
  const pressureStep = useRecoilValue(pressureStepSelector);
  const [selectedPressure, setSelectedPressure] = useRecoilState(
    selectedPressureState
  );

  /**
   * Monitor Height
   */
  const monitorHeightMinValue = useRecoilValue(monitorHeightMinValueSelector);
  const monitorHeightMaxValue = useRecoilValue(monitorHeightMaxValueSelector);
  const monitorHeightStep = useRecoilValue(monitorHeightStepSelector);
  const [selectedMonitorHeight, setSelectedMonitorHeight] = useRecoilState(
    selectedMonitorHeightState
  );

  /**
   * Angle
   */
  const angleMinValue = useRecoilValue(angleMinValueSelector);
  const angleMaxValue = useRecoilValue(angleMaxValueSelector);
  const angleStep = useRecoilValue(angleStepSelector);
  const [selectedAngle, setSelectedAngle] = useRecoilState(selectedAngleState);

  /**
   * Medium
   */
  const [selectedMedium, setSelectedMedium] =
    useRecoilState(selectedMediumState);
  const toggleSelectedMedium = () => {
    setSelectedMedium(selectedMedium === "water" ? "foam" : "water");
  };

  /**
   * Roof Obstacles
   */
  const [roofObstacles, setRoofObstacles] = useRecoilState(roofObstaclesState);

  const addRoofObstacle = () => {
    setRoofObstacles([
      ...roofObstacles,
      {
        distance:
          10 +
          (roofObstacles.length
            ? Math.max(...roofObstacles.map((e) => e.distance + e.depth))
            : 0),
        depth: 10,
        height: 10,
      },
    ]);
  };

  const removeRoofObstacle = (index: number) => {
    setRoofObstacles([
      ...roofObstacles.slice(0, index),
      ...roofObstacles.slice(index + 1),
    ]);
  };

  /**
   * Ground Obstacles
   */

  const [groundObstacles, setGroundObstacles] =
    useRecoilState(groundObstaclesState);

  const addGroundObstacle = () => {
    setGroundObstacles([
      ...groundObstacles,
      {
        distance:
          10 +
          (groundObstacles.length
            ? Math.max(...groundObstacles.map((e) => e.distance + e.depth))
            : 0),
        depth: 10,
        height: 10,
      },
    ]);
  };

  const removeGroundObstacle = (index: number) => {
    setGroundObstacles([
      ...groundObstacles.slice(0, index),
      ...groundObstacles.slice(index + 1),
    ]);
  };

  /**
   * If selectable monitors are available, but none is selected: select the first one
   */
  useEffect(() => {
    if (selectedMonitorId) {
      return;
    }
    const firstMonitor = selectableMonitors.at(0);
    if (!firstMonitor || !firstMonitor.id) {
      return;
    }
    setSelectedMonitorId(firstMonitor.id);
  }, [selectableMonitors, selectedMonitorId]);

  useEffect(() => {
    /**
     * If selectable nozzles are available, but none is selected: select the first one
     */

    const firstNozzle = selectableNozzles.at(0);

    if (!selectedNozzleId && firstNozzle && firstNozzle.id) {
      setSelectedNozzleId(firstNozzle.id);
    }

    /**
     * If selected nozzle id is not part of selectable nozzle ids: select the first one
     */
    if (
      selectedNozzleId &&
      !selectableNozzles.map((e) => e.id).includes(selectedNozzleId) &&
      firstNozzle &&
      firstNozzle.id
    ) {
      setSelectedNozzleId(firstNozzle.id);
    }
  }, [selectableNozzles, selectedNozzleId]);

  /**
   * Make sure that selected flow rate is between min and max, and respect the step
   */
  useEffect(() => {
    let revisedFlowRate = selectedFlowRate;

    /** must be >= min */
    revisedFlowRate = Math.max(revisedFlowRate, flowRateMinValue);

    /** must be <= max */
    revisedFlowRate = Math.min(revisedFlowRate, flowRateMaxValue);

    /** must be divisible by step */
    if (flowRateStep > 0) {
      const stepDivergence = revisedFlowRate % flowRateStep;
      revisedFlowRate -= stepDivergence;
    }

    if (revisedFlowRate !== selectedFlowRate) {
      setSelectedFlowRate(revisedFlowRate);
    }
  }, [selectedFlowRate, flowRateMinValue, flowRateMaxValue, flowRateStep]);

  /**
   * Make sure that selected pressure is between min and max, and respect the step
   */
  useEffect(() => {
    let revisedPressure = selectedPressure;

    /** must be >= min */
    revisedPressure = Math.max(revisedPressure, pressureMinValue);

    /** must be <= max */
    revisedPressure = Math.min(revisedPressure, pressureMaxValue);

    /** must be divisible by step */
    if (pressureStep > 0) {
      const stepDivergence = revisedPressure % pressureStep;
      revisedPressure -= stepDivergence;
    }

    if (revisedPressure !== selectedPressure) {
      setSelectedPressure(revisedPressure);
    }
  }, [selectedPressure, pressureMinValue, pressureMaxValue, pressureStep]);

  /**
   * Make sure that selected monitor height is between min and max, and respect the step
   */
  useEffect(() => {
    let revisedMonitorHeight = selectedMonitorHeight;

    /** must be >= min */
    revisedMonitorHeight = Math.max(
      revisedMonitorHeight,
      monitorHeightMinValue
    );

    /** must be <= max */
    revisedMonitorHeight = Math.min(
      revisedMonitorHeight,
      monitorHeightMaxValue
    );

    /** must be divisible by step */
    if (monitorHeightStep > 0) {
      const stepDivergence = revisedMonitorHeight % monitorHeightStep;
      revisedMonitorHeight -= stepDivergence;
    }

    if (revisedMonitorHeight !== selectedMonitorHeight) {
      setSelectedMonitorHeight(revisedMonitorHeight);
    }
  }, [
    selectedMonitorHeight,
    monitorHeightMinValue,
    monitorHeightMaxValue,
    monitorHeightStep,
  ]);

  /**
   * Make sure that selected angle is between min and max, and respect the step
   */

  const onAngleInputChange: NumberInputProps["onChange"] = (
    valueAsString: string
  ) => {
    setAngleInput(valueAsString);
  };

  const onAngleInputBlur: NumberInputProps["onBlur"] = (e) => {
    const value = parseFloat(e.target.value.replaceAll(",", "."));
    if (isNaN(value)) {
      // Input could not be parsed as number.
      // Reset input to value stored in state.
      setAngleInput(selectedAngle.toString());
    } else {
      // Input could be parsed as number.
      // Update store value.
      // Further validation is done in useEffects below.
      setSelectedAngle(value);
    }
  };

  useEffect(() => {
    let revisedAngle = selectedAngle;

    /** must be divisible by step */
    if (angleStep > 0) {
      const stepDivergence = revisedAngle % angleStep;
      const shouldRoundUp = stepDivergence > angleStep / 2;
      if (shouldRoundUp) {
        revisedAngle += angleStep - stepDivergence;
      } else {
        revisedAngle -= stepDivergence;
      }
    }

    /** must be >= min */
    revisedAngle = Math.max(revisedAngle, angleMinValue);

    /** must be <= max */
    revisedAngle = Math.min(revisedAngle, angleMaxValue);

    if (revisedAngle !== selectedAngle) {
      setSelectedAngle(revisedAngle);
    }
  }, [angleMinValue, angleMaxValue, angleStep, selectedAngle]);

  /**
   * Handle Angle Input
   */

  const [angleInput, setAngleInput] = useState<string>();

  /** Updated selectedAngle always updates angleInput */
  useEffect(() => {
    setAngleInput(selectedAngle.toString());
  }, [selectedAngle]);

  /** updated angleInput updates selectedAngle ONLY if it is valid. Otherwise typing into the field doesn't work properly. */
  useEffect(() => {
    if (!angleInput) {
      return;
    }
    const angleInputParsed = parseFloat(angleInput.replaceAll(",", "."));
    const angleInputIsValid =
      !isNaN(angleInputParsed) &&
      angleInputParsed >= angleMinValue &&
      angleInputParsed <= angleMaxValue &&
      angleInputParsed % angleStep === 0;
    if (angleInputIsValid) {
      setSelectedAngle(angleInputParsed);
    }
  }, [angleInput]);

  /**
   * Handle obstacle changes
   */

  const onRoofObstacleValueChange = (
    index: number,
    valueType: keyof ObstacleInput,
    newValue: number
  ) => {
    if (isNaN(newValue)) {
      newValue = 0;
    }
    setRoofObstacles([
      ...roofObstacles.slice(0, index),
      {
        ...roofObstacles[index],
        [valueType]: newValue,
      },
      ...roofObstacles.slice(index + 1),
    ]);
  };

  const onGroundObstacleValueChange = (
    index: number,
    valueType: keyof ObstacleInput,
    newValue: number
  ) => {
    if (isNaN(newValue)) {
      newValue = 0;
    }
    setGroundObstacles([
      ...groundObstacles.slice(0, index),
      {
        ...groundObstacles[index],
        [valueType]: newValue,
      },
      ...groundObstacles.slice(index + 1),
    ]);
  };

  return flowRateMinValue &&
    flowRateMaxValue &&
    flowRateStep &&
    selectedFlowRate &&
    pressureMinValue &&
    pressureMaxValue &&
    pressureStep &&
    selectedPressure ? (
    <>
      <Heading variant="redDot" size="sm">
        {t("components.settings.title")}
      </Heading>
      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap="8">
        <GridItem bg="white" padding="5">
          <Grid>
            <GridItem>
              <GrayDotBox>
                <Select
                  variant="headline"
                  onChange={(e) => setSelectedMonitorId(e.target.value)}
                >
                  {selectableMonitors.map((monitor) => (
                    <option key={monitor.id} value={monitor.id}>
                      {t("components.settings.monitorOption", {
                        monitorName: monitor.name[currentLanguage],
                      })}
                    </option>
                  ))}
                </Select>
              </GrayDotBox>

              {selectedMonitor?.description && (
                <Text marginTop={4} marginBottom={4}>
                  {selectedMonitor.description[currentLanguage][unit]}
                </Text>
              )}
            </GridItem>
            <GridItem>
              <GrayDotBox>
                <Select
                  variant="headline"
                  onChange={(e) => setSelectedNozzleId(e.target.value)}
                >
                  {selectableNozzles.map((nozzle) => (
                    <option key={nozzle.id} value={nozzle.id}>
                      {t("components.settings.nozzleOption", {
                        nozzleName: nozzle.name[currentLanguage],
                      })}
                    </option>
                  ))}
                </Select>
              </GrayDotBox>
              {selectedNozzle?.description && (
                <Text marginTop={4} marginBottom={4}>
                  {selectedNozzle.description[currentLanguage][unit]}
                </Text>
              )}
            </GridItem>
            <GridItem>
              <Heading size="sm" variant="underline">
                {t("components.settings.headlineAngle")}
              </Heading>
              <Grid templateColumns={{ base: "1fr", md: "1fr 3fr" }}>
                <GridItem>
                  <NumberInput
                    inputMode="decimal"
                    min={angleMinValue}
                    max={angleMaxValue}
                    step={angleStep}
                    value={angleInput}
                    onChange={onAngleInputChange}
                    isValidCharacter={(value) =>
                      value.match(/^[Ee0-9+\-.,]$/) ? true : false
                    }
                    onBlur={onAngleInputBlur}
                    format={(value) => {
                      const formattedValue = t(
                        "formatting.numberMaxTwoDecimalPoints",
                        { number: value }
                      );
                      return formattedValue === "NaN" ? value : formattedValue;
                    }}
                    keepWithinRange={true}
                    clampValueOnBlur={false}
                    onKeyUp={(e) => {
                      // Handle keys 'Enter' and 'NumpadEnter'
                      if (e.code.toLowerCase().includes("enter")) {
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </GridItem>
                {selectedMonitorImage && selectedMonitorNozzleImage && (
                  <GridItem paddingTop={{ base: "10", md: "5" }}>
                    <AngleGui
                      monitorImage={selectedMonitorImage}
                      nozzleImage={selectedMonitorNozzleImage}
                      displayMonitorImageAboveNozzle={
                        selectedMonitor?.displayImageAboveNozzle ?? false
                      }
                    />
                  </GridItem>
                )}
              </Grid>
            </GridItem>
          </Grid>
        </GridItem>
        <GridItem bg="white" padding="5">
          <Grid rowGap={5}>
            <GridItem>
              <Heading variant="underline">
                {t("components.settings.headlineMedium")}
              </Heading>
              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="mediumSwitch" mb="0">
                  {t("components.settings.mediumWater")}
                </FormLabel>
                <Switch
                  id="mediumSwitch"
                  variant="mediumSwitch"
                  colorScheme="brand.blue"
                  size="lg"
                  isChecked={selectedMedium === "water"}
                  onChange={toggleSelectedMedium}
                />
                <FormLabel htmlFor="mediumSwitch" mb="0" ml="3">
                  {t("components.settings.mediumFoam")}
                </FormLabel>
              </FormControl>
            </GridItem>
            <GridItem>
              <Grid templateColumns="1fr 1fr">
                <GridItem>
                  <Heading variant="grayDot">
                    {t("components.settings.headlineExtinguishingAgentFlow")}
                  </Heading>
                </GridItem>
                <GridItem>
                  <Text align="right">
                    {t("formatting.numberMaxTwoDecimalPoints", {
                      number: unitFormatter(
                        selectedFlowRate,
                        UnitTypes.Liter,
                        unit
                      ),
                    })}{" "}
                    {t(
                      unit === Units.Metric
                        ? "units.literPerMinute"
                        : "units.gallonPerMinute"
                    )}
                  </Text>
                </GridItem>
              </Grid>
              <Slider
                min={flowRateMinValue}
                max={flowRateMaxValue}
                step={flowRateStep}
                value={selectedFlowRate}
                onChange={(value) => setSelectedFlowRate(value)}
                variant="outlineGray"
              >
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb boxSize={6} />
              </Slider>
            </GridItem>
            <GridItem>
              <Grid templateColumns="2fr 1fr">
                <GridItem>
                  <Heading variant="grayDot">
                    {t("components.settings.headlinePressure")}
                  </Heading>
                </GridItem>
                <GridItem>
                  <Text align="right">
                    {t("formatting.numberMaxTwoDecimalPoints", {
                      number: unitFormatter(
                        selectedPressure,
                        UnitTypes.Bar,
                        unit
                      ),
                    })}{" "}
                    {t(unit === Units.Metric ? "units.bar" : "units.psi")}
                  </Text>
                </GridItem>
              </Grid>

              <Slider
                min={pressureMinValue}
                max={pressureMaxValue}
                step={pressureStep}
                value={selectedPressure}
                onChange={(value) => setSelectedPressure(value)}
                variant="outlineGray"
              >
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb boxSize={6} />
              </Slider>
            </GridItem>
            <GridItem>
              <Grid templateColumns="1fr 1fr">
                <GridItem>
                  <Heading variant="grayDot">
                    {t("components.settings.headlineMonitorHeight")}
                  </Heading>
                </GridItem>
                <GridItem>
                  <Text align="right">
                    {t("formatting.numberMaxTwoDecimalPoints", {
                      number: unitFormatter(
                        selectedMonitorHeight,
                        UnitTypes.Meter,
                        unit
                      ),
                    })}{" "}
                    {t(unit === Units.Metric ? "units.meter" : "units.foot")}
                  </Text>
                </GridItem>
              </Grid>
              <Slider
                min={monitorHeightMinValue}
                max={monitorHeightMaxValue}
                step={monitorHeightStep}
                value={selectedMonitorHeight}
                onChange={(value) => setSelectedMonitorHeight(value)}
                variant="outlineGray"
              >
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb boxSize={6} />
              </Slider>
            </GridItem>
            <GridItem>
              <Heading variant="underline">
                {t("components.settings.headlineRoofObstacle")}
              </Heading>
              <Grid rowGap={5}>
                {roofObstacles.map((obstacle, i) => (
                  <GridItem key={i} marginBottom="5">
                    <Grid templateColumns={{ base: "1fr", xl: "1fr 2fr" }}>
                      <GridItem>
                        <HStack>
                          <RemoveButton
                            onClick={() => {
                              removeRoofObstacle(i);
                            }}
                          />
                          <Heading>
                            {maxNumberOfRoofObstacles > 1
                              ? t(
                                  "components.settings.obstacleLabelWithNumber",
                                  { obstacleNumber: i + 1 }
                                )
                              : t("components.settings.obstacleLabel")}
                          </Heading>
                        </HStack>
                      </GridItem>
                      <GridItem marginTop={{ base: "5", xl: "0" }}>
                        <ObstacleFormInput
                          obstacle={obstacle}
                          onValueChange={(valueType, newValue) => {
                            onRoofObstacleValueChange(i, valueType, newValue);
                          }}
                        />
                      </GridItem>
                    </Grid>
                  </GridItem>
                ))}
                {roofObstacles.length < maxNumberOfRoofObstacles && (
                  <GridItem>
                    <AddButton
                      onClick={() => {
                        addRoofObstacle();
                      }}
                    />
                  </GridItem>
                )}
              </Grid>
            </GridItem>
            <GridItem>
              <Heading variant="underline">
                {t("components.settings.headlineGroundObstacle")}
              </Heading>
              <Grid rowGap={5}>
                {groundObstacles.map((obstacle, i) => (
                  <GridItem key={i} marginBottom="5">
                    <Grid templateColumns={{ base: "1fr", xl: "1fr 2fr" }}>
                      <GridItem>
                        <HStack>
                          <RemoveButton
                            onClick={() => {
                              removeGroundObstacle(i);
                            }}
                          />
                          <Heading>
                            {maxNumberOfGroundObstacles > 1
                              ? t(
                                  "components.settings.obstacleLabelWithNumber",
                                  { obstacleNumber: i + 1 }
                                )
                              : t("components.settings.obstacleLabel")}
                          </Heading>
                        </HStack>
                      </GridItem>
                      <GridItem marginTop={{ base: "5", xl: "0" }}>
                        <ObstacleFormInput
                          obstacle={obstacle}
                          onValueChange={(valueType, newValue) => {
                            onGroundObstacleValueChange(i, valueType, newValue);
                          }}
                        />
                      </GridItem>
                    </Grid>
                  </GridItem>
                ))}
                {groundObstacles.length < maxNumberOfGroundObstacles && (
                  <GridItem>
                    <AddButton
                      onClick={() => {
                        addGroundObstacle();
                      }}
                    />
                  </GridItem>
                )}
              </Grid>
            </GridItem>
          </Grid>
        </GridItem>
      </Grid>
    </>
  ) : (
    <Spinner />
  );
};

export default Settings;
